import React from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { get, isNil } from 'lodash-es';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectNShowcase } from '@/store/slices/userPreferences';
import {
  authenticationChangePasswordConfirm,
  authenticationChangePasswordRequest,
} from '@/store/slices/authentication';

import { ROUTE } from '../../routes';
import { Text } from '../../components/Text';
import { Divider } from '../../components/Divider';
import { useLanguageSwitcher } from '../../hooks/useLanguageSwitcher';
import { useAuthGate } from '../../layouts/useAuthGate';
import { AuthLogo } from '../login/components/AuthLogo';
import { LanguagePicker } from '../login/components/LanguagePicker';
import { useAuthCardMorph } from '../login/useAuthCardMorph';
import { scorePassword } from '../../utils/password-strength';
import { ChangePassword } from './change-password.styles';

// Intro: explain the flow, request an emailed code -> `intro` -> `code`. Confirm half (arrived
// with the emailed `?code=` link, or after requesting): enter the code + a new password -> `code`
// -> `done`. The two halves share the page shell.
type ChangeLayout = 'intro' | 'code' | 'done';

// The confirm failure the notice branches on. `expired`/`invalid` map to the BE machine codes;
// `generic` covers everything else (network, unknown code, absent code).
type ConfirmError = 'expired' | 'invalid' | 'generic';

/**
 * `/change-password` (SPB-480): authenticated change-your-password flow.
 *
 * Hand-written, strictly-typed sibling of `/reset-password`, reusing the `/login` + `/register`
 * design language and their shared pieces (AuthLogo, LanguagePicker, useAuthCardMorph). Unlike
 * `/reset-password`, this page IS auth-gated in-page: only a signed-in owner can change their
 * password. The flow: request an emailed confirmation code
 * (`authenticationChangePasswordRequest`), then set a new password with that code
 * (`authenticationChangePasswordConfirm`). The BE invalidates only the owner's OTHER sessions on
 * success, so THIS session survives and the confirmation sends the owner back to settings (not to
 * sign-in). The 401 interceptor excludes `/auth/`, so there is no global self-heal on failure:
 * every failure is surfaced locally.
 *
 * SINGLE mount at `/change-password` (see `router.tsx`): the BE emails the hyphen path
 * `/change-password?code=`, so there is no underscore alias (unlike reset-password's dual mount).
 * The emailed code is valid for 1h; a signed-out owner who clicks it is sent to `/login`, and
 * re-clicking the same link after signing back in is the recovery path (no return-URL plumbing).
 */
const PageChangePassword = (): React.ReactElement => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { authenticated, gate } = useAuthGate();
  // Signed in here, so the owner's Newton showcase preference applies to the lockup.
  const newtonShowcase = useAppSelector(selectNShowcase);
  const { languages, currentLanguage, onChangeLanguage } = useLanguageSwitcher();
  const { innerRef, cardStyle } = useAuthCardMorph();

  // The emailed change-password link lands here as `/change-password?code=…`. A present, non-empty
  // code opens the enter-code view with the field prefilled; its absence keeps the intro view.
  // `?code=` with no value yields '' and counts as absent.
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get('code');
  const hasCode = !isNil(code) && code.length > 0;

  const [layout, setLayout] = React.useState<ChangeLayout>(hasCode ? 'code' : 'intro');
  const [codeValue, setCodeValue] = React.useState<string>(hasCode ? (code ?? '') : '');
  const [password, setPassword] = React.useState<string>('');
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  // Intro/resend request failure (there is no self-heal, the 401 interceptor excludes `/auth/`).
  const [requestError, setRequestError] = React.useState<boolean>(false);
  // Confirm failure, branched onto the notice copy.
  const [confirmError, setConfirmError] = React.useState<ConfirmError | null>(null);
  const strength = React.useMemo(() => scorePassword(password), [password]);

  // Guards a double-click from firing two requests while the first is in flight.
  const submittingRef = React.useRef(false);

  // Request half (intro primary + resend): ask the BE to email a fresh code. On success open the
  // enter-code view; on failure surface the local notice (no global self-heal).
  const handleRequest = React.useCallback(() => {
    if (submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    setRequestError(false);
    // A resend from the `code` view also clears any stale confirm-error notice so the fresh
    // request starts clean (otherwise a prior "wrong code" notice lingers over the resend).
    setConfirmError(null);
    setSubmitting(true);

    void dispatch(authenticationChangePasswordRequest())
      .unwrap()
      .then(() => {
        setLayout('code');
      })
      .catch(() => {
        setRequestError(true);
      })
      .finally(() => {
        submittingRef.current = false;
        setSubmitting(false);
      });
  }, [dispatch]);

  const handleCodeChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setCodeValue(event.target.value);
    setConfirmError(null);
  }, []);

  const handlePasswordChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setPassword(event.target.value);
    // Editing the password clears a prior failure so the owner can retry.
    setConfirmError(null);
  }, []);

  const canSubmitPassword = codeValue.trim().length > 0 && password.length >= 8 && !submitting;

  // Confirm half: set the new password with the emailed code. On success drop the (consumed) code
  // from the URL and swap to the confirmation. On failure branch the notice on the BE machine code.
  const handleConfirmSubmit = React.useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault();

      if (codeValue.trim().length === 0 || password.length < 8 || submittingRef.current) {
        return;
      }

      submittingRef.current = true;
      setConfirmError(null);
      // Clear a stale resend-failure notice too: the confirm-error branch must be able to show,
      // and the requestError notice takes render precedence over it.
      setRequestError(false);
      setSubmitting(true);

      void dispatch(authenticationChangePasswordConfirm({ code: codeValue, new_password: password }))
        .unwrap()
        .then(() => {
          // Drop the (now consumed) code from the URL so it does not linger in history, and so a
          // refresh on the confirmation lands on the intro rather than re-opening the enter-code
          // view with a dead code.
          setSearchParams({}, { replace: true });
          setLayout('done');
        })
        .catch((rejected: unknown) => {
          // The reject payload (AuthenticationLinkError) carries the BE machine `code`; branch the
          // notice on it, same read as the /login password thunk (`get(rejected, 'code')`).
          const rejectedCode = get(rejected, 'code');
          setConfirmError(
            rejectedCode === 'VERIFICATION_CODE_EXPIRED'
              ? 'expired'
              : rejectedCode === 'INVALID_VERIFICATION_CODE'
                ? 'invalid'
                : 'generic',
          );
        })
        .finally(() => {
          submittingRef.current = false;
          setSubmitting(false);
        });
    },
    [dispatch, codeValue, password, setSearchParams],
  );

  // Auth gate LAST, after every hook is declared (never conditionally before the hooks above).
  // `gate === null` means boot rehydration is still resolving: render nothing, do NOT redirect
  // (a hard refresh must not bounce a signed-in owner). Once settled and unauthenticated, send to
  // `/login` (the hook's own default is `/register`; this owner has an account, so `/login`). The
  // emailed code is valid for 1h, so re-clicking the link after signing in is the recovery path.
  if (gate === null) {
    return <React.Fragment />;
  }
  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  // Root class is `nova-change-password` (non-colliding), same reason register uses
  // `nova-register`: keeps this page fully self-styled.
  return (
    <ChangePassword className="nova-change-password">
      <div className="page">
        <div className="main">
          <div className="header">
            <AuthLogo newton={newtonShowcase} />
          </div>

          <div className="body" style={cardStyle}>
            <div className="body-inner" ref={innerRef}>
              <div className="layout">
                {layout === 'intro' ? (
                  <div className="wrapper form-view">
                    <div className="sign-in-form">
                      <div className="heading">
                        <Text className="title" text="Change your password" />
                        <Text
                          className="subtitle"
                          text="We will email you a confirmation code to set a new password."
                        />
                      </div>

                      {requestError ? (
                        <div className="link-notice" role="alert">
                          <i className="bi bi-exclamation-circle-fill notice-icon" aria-hidden="true" />
                          <div className="notice-copy">
                            <Text className="notice-title" text="We couldn't send the code. Please try again." />
                          </div>
                        </div>
                      ) : null}

                      <div className="cta">
                        <button
                          type="button"
                          className={`button${submitting ? ' disabled' : ''}`}
                          disabled={submitting}
                          onClick={handleRequest}
                        >
                          <Text className="text" text="Email me a code" />
                          <i className="bi bi-arrow-right icon" aria-hidden="true" />
                        </button>
                      </div>
                    </div>

                    <Divider variant="styled" className="auth-divider" />

                    <div className="footer">
                      <Link className="link" to={ROUTE.settingsSection('account')}>
                        <Text text="Back to settings" />
                      </Link>
                    </div>
                  </div>
                ) : layout === 'code' ? (
                  <div className="wrapper form-view">
                    <form className="sign-in-form" onSubmit={handleConfirmSubmit}>
                      <div className="heading">
                        <Text className="title" text="Enter your code" />
                        <Text
                          className="subtitle"
                          text="Enter the code we emailed you and choose a new password."
                        />
                      </div>

                      {requestError ? (
                        // A failed resend from this view (network drop, or the session token expired
                        // so change-password-request 401s — no self-heal, the interceptor skips
                        // `/auth/`). Without this the resend would fail silently on the code view.
                        <div className="link-notice" role="alert">
                          <i className="bi bi-exclamation-circle-fill notice-icon" aria-hidden="true" />
                          <div className="notice-copy">
                            <Text className="notice-title" text="We couldn't send the code. Please try again." />
                          </div>
                        </div>
                      ) : confirmError ? (
                        <div className="link-notice" role="alert">
                          <i className="bi bi-exclamation-circle-fill notice-icon" aria-hidden="true" />
                          <div className="notice-copy">
                            {confirmError === 'expired' ? (
                              <Text className="notice-title" text="This code has expired. Request a new one below." />
                            ) : confirmError === 'invalid' ? (
                              <Text
                                className="notice-title"
                                text="That code is not right. Check the code from the email and try again."
                              />
                            ) : (
                              <Text
                                className="notice-title"
                                text="We couldn't change your password. Please try again."
                              />
                            )}
                          </div>
                        </div>
                      ) : null}

                      <div className="input">
                        <input
                          className="code-field"
                          type="text"
                          name="code"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          placeholder={t('6-digit code')}
                          value={codeValue}
                          onChange={handleCodeChange}
                        />

                        <div className="resend">
                          <Text className="resend-label" text="Didn't get it?" />
                          <button
                            type="button"
                            className="resend-link"
                            disabled={submitting}
                            onClick={handleRequest}
                          >
                            <Text text="Resend code" />
                          </button>
                        </div>

                        <div className="password-input">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="new-password"
                            autoComplete="new-password"
                            placeholder={t('Create a password')}
                            value={password}
                            onChange={handlePasswordChange}
                            maxLength={128}
                          />
                          <button
                            type="button"
                            className="eye-toggle"
                            aria-label={t(showPassword ? 'Hide password' : 'Show password')}
                            onClick={() => setShowPassword((v) => !v)}
                          >
                            <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden="true" />
                          </button>
                        </div>

                        {/* Strength meter: reserved height, hidden until the first keystroke, uniform
                          colour per state. Bars are decorative (aria-hidden); the caption carries the
                          meaning. Same behaviour as register's meter. */}
                        <div
                          className={`password-strength strength-${strength.level}${
                            password.length > 0 ? ' is-visible' : ''
                          }`}
                        >
                          <div className="strength-bars" aria-hidden="true">
                            <span className={`bar${strength.score >= 1 ? ' filled' : ''}`} />
                            <span className={`bar${strength.score >= 2 ? ' filled' : ''}`} />
                            <span className={`bar${strength.score >= 3 ? ' filled' : ''}`} />
                          </div>
                          <span className="strength-caption" aria-live="polite">
                            {password.length === 0 ? null : strength.level === 'too-short' ? (
                              <Text text="Use at least 8 characters." />
                            ) : strength.level === 'weak' ? (
                              <Text text="Weak password" />
                            ) : strength.level === 'fair' ? (
                              <Text text="Fair password" />
                            ) : (
                              <Text text="Strong password" />
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="cta">
                        <button
                          type="submit"
                          className={`button${canSubmitPassword ? '' : ' disabled'}`}
                          disabled={!canSubmitPassword}
                        >
                          <Text className="text" text="Change password" />
                          <i className="bi bi-arrow-right icon" aria-hidden="true" />
                        </button>
                      </div>
                    </form>

                    <Divider variant="styled" className="auth-divider" />

                    <div className="footer">
                      <Link className="link" to={ROUTE.settingsSection('account')}>
                        <Text text="Back to settings" />
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="wrapper sent-view">
                    <div className="heading">
                      <div className="heading-copy">
                        <Text className="title" text="Your password has been changed." />
                        <Text className="subtitle" text="You are still signed in on this device." />
                      </div>
                    </div>

                    <Divider variant="styled" className="auth-divider" />

                    <div className="footer">
                      <Link className="link" to={ROUTE.settingsSection('account')}>
                        <Text text="Back to settings" />
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <LanguagePicker languages={languages} currentLanguage={currentLanguage} onChange={onChangeLanguage} />
        </div>
      </div>
    </ChangePassword>
  );
};

export default PageChangePassword;
